import {
  PublicClientApplication,
  AuthenticationResult,
  ProtocolMode,
  LogLevel,
} from '@azure/msal-browser';
import { localStorageService } from './localstorage.service';
import { observationDraftService } from './observationDraftService';
import { logger } from '../utils/logger';
import {
  buildInteractiveLoginRequest,
  createMobileLoginRequest,
  createSilentTokenRequest,
} from './auth/loginRequestBuilder';
import {
  initializeMsalErrorHandling,
  handleMsalError,
  clearMsalErrors,
  clearStaleMsalCache,
} from './auth/msalErrorHandler';
// session imports this file too; each uses the other only inside functions, never at load.
import { session } from './auth/session';

// Check required environment variables
// [existing environment variable checks]

// Validate redirect URI configuration
const validateRedirectUri = (): string => {
  const envRedirectUri = import.meta.env.VITE_AZURE_REDIRECT_URI;
  const currentOrigin = window.location.origin;

  if (envRedirectUri) {
    // Ensure no trailing slash mismatch
    const normalizedEnvUri = envRedirectUri.replace(/\/$/, '');
    const normalizedOrigin = currentOrigin.replace(/\/$/, '');

    if (normalizedEnvUri !== normalizedOrigin) {
      logger.warn(
        `Redirect URI mismatch: env=${normalizedEnvUri}, origin=${normalizedOrigin}`,
      );
      logger.info('Using environment variable VITE_AZURE_REDIRECT_URI');
    }
    return normalizedEnvUri;
  }

  logger.info('No VITE_AZURE_REDIRECT_URI set, using window.location.origin');
  return currentOrigin;
};

// Azure AD configuration from environment variables
const msalConfig = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID,
    authority: import.meta.env.VITE_AZURE_AUTHORITY,
    redirectUri: validateRedirectUri(),
    postLogoutRedirectUri: window.location.origin,
    protocolMode: ProtocolMode.OIDC,
    navigateToLoginRequestUrl: true, // Allow navigation for redirect flow
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: true,
  },
  system: {
    // Setting the token renewal offset to refresh token 5 minutes before expiration
    tokenRenewalOffsetSeconds: 300,
    loggerOptions: {
      // [existing logger options]
      loggerCallback: (
        level: LogLevel,
        message: string,
        containsPii: boolean,
      ) => {
        if (containsPii) return;
        if (level >= 3) {
          console.error(message);
        } else if (level === 2) {
          console.warn(message);
        } else if (level === 1) {
          console.info(message);
        } else {
          console.debug(message);
        }
      },
      logLevel: parseInt(import.meta.env.VITE_AZURE_AUTH_DEBUG_LEVEL || '2'),
    },
  },
};

// MSAL instance singleton
let msalInstanceSingleton: PublicClientApplication | null = null;
let isInitializing = false;
let initializationPromise: Promise<void> | null = null;

// Get or create MSAL instance
const getMsalInstance = (): PublicClientApplication => {
  if (!msalInstanceSingleton) {
    logger.info('Creating MSAL instance...');
    msalInstanceSingleton = new PublicClientApplication(msalConfig);
  }
  return msalInstanceSingleton;
};

// Export for backward compatibility
export const msalInstance = getMsalInstance();

// Login request parameters using the builder pattern
const loginRequest = createMobileLoginRequest();

// acquireTokenRedirect navigates away, and the visibilitychange/pagehide flush in
// observationDraftService is not awaited, so field edits still inside the draft
// debounce would be lost. An edit typed while a flush is writing is still dirty
// when that flush resolves, so flush again until nothing is pending. A failed
// flush, or edits that never settle, are logged but must not block sign-in.
const MAX_DRAFT_FLUSHES_BEFORE_REDIRECT = 3;

export const flushDraftsBeforeRedirect = async (): Promise<void> => {
  try {
    for (let pass = 0; pass < MAX_DRAFT_FLUSHES_BEFORE_REDIRECT; pass++) {
      await observationDraftService.flushAll();
      if (!observationDraftService.hasPendingChanges()) {
        return;
      }
    }
    logger.error(
      `Field drafts still unsaved after ${MAX_DRAFT_FLUSHES_BEFORE_REDIRECT} saves before token redirect`,
    );
  } catch (error) {
    logger.error(
      'Failed to save field drafts before token redirect: ' +
        (error instanceof Error ? error.message : String(error)),
    );
  }
};

// Wrap MSAL login with our own implementation
const azureAuthService = {
  // Process redirect response and store token
  processRedirectResponse: async (response: AuthenticationResult) => {
    try {
      const instance = getMsalInstance();

      // Ensure we have the latest token
      const account = instance.getAllAccounts()[0];
      if (!account) {
        logger.error('No account found after redirect');
        return null;
      }

      const silentRequest = createSilentTokenRequest(account);
      const tokenResponse = await instance.acquireTokenSilent(silentRequest);

      if (tokenResponse && tokenResponse.accessToken) {
        session.recordSignIn(tokenResponse.accessToken);
      }

      return response;
    } catch (error) {
      logger.error(
        'Error processing redirect response: ' +
          (error instanceof Error ? error.message : String(error)),
      );
      return null;
    }
  },

  // Initialize MSAL instance and handle redirect flow
  initialize: async () => {
    const instance = getMsalInstance();

    // Initialize error handling
    initializeMsalErrorHandling();

    // Prevent duplicate initialization
    if (isInitializing && initializationPromise) {
      logger.debug('MSAL initialization already in progress, waiting...');
      await initializationPromise;

      // After waiting, try to handle redirect
      try {
        const response = await instance.handleRedirectPromise();
        if (response) {
          return await azureAuthService.processRedirectResponse(response);
        }
      } catch (error) {
        logger.error(
          'Error handling redirect after waiting for init: ' +
            (error instanceof Error ? error.message : String(error)),
        );
      }
      return null;
    }

    // Check if already initialized
    let needsInit = false;
    try {
      const accounts = instance.getAllAccounts();
      // If we can get accounts, it's initialized
      logger.debug(
        'MSAL instance already initialized with ' +
          accounts.length +
          ' accounts',
      );
    } catch (e) {
      needsInit = true;
    }

    if (needsInit) {
      // Not initialized yet, initialize now
      logger.info('Initializing MSAL instance...');

      // Clear any stale cache before initialization
      clearStaleMsalCache();

      isInitializing = true;
      initializationPromise = instance
        .initialize()
        .then(() => {
          isInitializing = false;
          initializationPromise = null;
          logger.info('MSAL initialization successful');
        })
        .catch((error) => {
          isInitializing = false;
          initializationPromise = null;
          logger.error(
            'MSAL initialization failed: ' +
              (error instanceof Error ? error.message : String(error)),
          );
          handleMsalError(error);
          throw error;
        });
      await initializationPromise;
    }

    logger.info('Handling redirect promise...');
    try {
      // This returns the response if this page load is the result of a redirect
      const response = await instance.handleRedirectPromise().catch((error) => {
        logger.error(
          'Error handling redirect promise: ' +
            (error instanceof Error ? error.message : String(error)),
        );
        if (handleMsalError(error)) {
          // Error was handled, try once more
          return instance.handleRedirectPromise();
        }
        throw error;
      });

      if (response) {
        logger.info('Redirect response received');
        return await azureAuthService.processRedirectResponse(response);
      } else {
        logger.info('No redirect response');

        const existingToken = session.getToken();
        if (existingToken) {
          session.recordSignIn(existingToken);
        }
      }
    } catch (error) {
      logger.error(
        'Error handling redirect: ' +
          (error instanceof Error ? error.message : String(error)),
      );
    }

    return null; // No redirect was handled
  },

  // Login using redirect with improved token handling
  loginRedirect: async (request = loginRequest): Promise<void> => {
    try {
      // Build standardized login request
      const enhancedRequest = buildInteractiveLoginRequest(request);
      logger.info(
        'Attempting to login with scopes: ' + enhancedRequest.scopes?.join(' '),
      );

      localStorageService.clearMsalItems();

      // Clear any error states before login
      clearMsalErrors();

      // Perform the login with redirect
      await getMsalInstance().loginRedirect(enhancedRequest);

      // Note: This method will cause a page redirect, so code after this won't execute
      logger.info('Login redirect initiated');
    } catch (error) {
      logger.error(
        'Login redirect error: ' +
          (error instanceof Error ? error.message : String(error)),
      );
      handleMsalError(error);
      throw error;
    }
  },

  // Logout from the application only (not from Microsoft)
  logout: async () => {
    try {
      logger.info('Logging out user from application...');

      // Store rememberedUser value before logout if it exists
      const rememberedUser = localStorage.getItem('rememberedUser');

      // IMPORTANT: Clear MSAL accounts to properly logout
      const msalInstance = getMsalInstance();
      const accounts = msalInstance.getAllAccounts();

      if (accounts.length > 0) {
        logger.info(
          `Clearing ${accounts.length} MSAL account(s) from cache...`,
        );

        // Set active account to null
        msalInstance.setActiveAccount(null);

        // Clear MSAL cache completely
        // This is the most reliable way to clear authentication state
        try {
          await msalInstance.clearCache();
          logger.info('Cleared MSAL cache successfully');
        } catch (e) {
          // Fallback: manually clear all MSAL storage items
          logger.warn(
            'clearCache failed, manually clearing MSAL storage: ' +
              (e instanceof Error ? e.message : String(e)),
          );

          // Clear all MSAL-related items from localStorage
          const keysToRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.includes('msal') || key.includes('login-hint'))) {
              keysToRemove.push(key);
            }
          }

          keysToRemove.forEach((key) => {
            localStorage.removeItem(key);
            logger.debug(`Removed localStorage key: ${key}`);
          });

          // Also clear sessionStorage
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && (key.includes('msal') || key.includes('login-hint'))) {
              sessionStorage.removeItem(key);
              logger.debug(`Removed sessionStorage key: ${key}`);
            }
          }
        }

        logger.info('Cleared all MSAL accounts');
      }

      // Clear all MSAL localStorage items
      localStorageService.clearMsalItems();

      // Restore rememberedUser if it existed
      if (rememberedUser) {
        localStorage.setItem('rememberedUser', rememberedUser);
        logger.info('Preserved remembered username during logout');
      }

      logger.info('Cleared MSAL cache and localStorage');

      // Clear IndexedDB databases
      const databases = await indexedDB.databases();
      databases.forEach(async (db) => {
        db.name && indexedDB.deleteDatabase(db.name);
      });

      // Clear sync date
      localStorage.removeItem('lastSyncDate');
      localStorage.removeItem('lastSyncResult');

      // Try to clear any additional MSAL browser storage
      try {
        // Clear any cached MSAL data in sessionStorage
        Object.keys(sessionStorage).forEach((key) => {
          if (key.includes('msal')) {
            sessionStorage.removeItem(key);
          }
        });
        logger.debug('Cleared MSAL sessionStorage items');
      } catch (e) {
        logger.debug(
          'Could not clear sessionStorage: ' +
            (e instanceof Error ? e.message : String(e)),
        );
      }

      logger.info(
        'User logged out from application successfully - MSAL state cleared',
      );
    } catch (error) {
      logger.error(
        'Logout error: ' +
          (error instanceof Error ? error.message : String(error)),
      );
    }
  },

  // Helper methods for "Remember Me" functionality
  rememberMe: {
    // [existing rememberMe implementation]
    saveUsername: (username: string) => {
      try {
        if (username && username.trim()) {
          localStorage.setItem('rememberedUser', username.trim());
          logger.info('Username saved for "Remember Me"');
          return true;
        }
        return false;
      } catch (error) {
        logger.error(
          'Error saving remembered username: ' +
            (error instanceof Error ? error.message : String(error)),
        );
        return false;
      }
    },

    getUsername: (): string | null => {
      try {
        return localStorage.getItem('rememberedUser');
      } catch (error) {
        logger.error(
          'Error getting remembered username: ' +
            (error instanceof Error ? error.message : String(error)),
        );
        return null;
      }
    },

    clearUsername: () => {
      try {
        localStorage.removeItem('rememberedUser');
        logger.info('Remembered username cleared');
        return true;
      } catch (error) {
        logger.error(
          'Error clearing remembered username: ' +
            (error instanceof Error ? error.message : String(error)),
        );
        return false;
      }
    },

    hasRememberedUser: (): boolean => {
      return localStorage.getItem('rememberedUser') !== null;
    },
  },
};

export default azureAuthService;
